import { useMemo, useState, type FormEvent } from "react";
import { confirmSignIn, type SignInOutput } from "aws-amplify/auth";
import { QRCodeSVG } from "qrcode.react";
import { ErrorMessage } from "./error-message.js";
import { friendlyAuthError } from "./cognito-errors.js";

/**
 * Renders when Amplify returns `nextStep.signInStep ===
 * 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP'` — the user pool has REQUIRED
 * MFA but the user hasn't set up TOTP yet (first sign-in after
 * sign-up).
 *
 * Flow:
 *   1. Read totpSetupDetails from the nextStep payload.
 *   2. Render a QR code via getSetupUri('Hearth', email) → otpauth:// URL.
 *   3. Show the manual `sharedSecret` for users who can't scan.
 *   4. User types the 6-digit code shown by their authenticator.
 *   5. confirmSignIn({ challengeResponse }) verifies.
 *
 * The `accountName` arg to getSetupUri is what the authenticator app
 * displays alongside "Hearth". Using the user's email gives them a
 * recognizable label when they have many TOTP entries.
 */

type TotpSetupNextStep = Extract<
  SignInOutput["nextStep"],
  { signInStep: "CONTINUE_SIGN_IN_WITH_TOTP_SETUP" }
>;

interface Props {
  nextStep: TotpSetupNextStep;
  accountName: string;
  onSignedIn: () => void;
}

export function MfaSetupFlow({ nextStep, accountName, onSignedIn }: Props) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);

  const setupUri = useMemo(
    () => nextStep.totpSetupDetails.getSetupUri("Hearth", accountName).toString(),
    [nextStep.totpSetupDetails, accountName],
  );
  const sharedSecret = nextStep.totpSetupDetails.sharedSecret;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await confirmSignIn({ challengeResponse: code });
      if (result.isSignedIn) {
        // updateMFAPreference moved to DashboardPage's mount effect.
        // Calling it here races with Amplify's token-storage finish
        // line and tends to fail silently, leaving Cognito's
        // UserMFASettingList null. Setting it after dashboard load,
        // when tokens are reliably persisted, works without races.
        onSignedIn();
      } else {
        setError(
          `Unexpected next step after TOTP setup: ${result.nextStep.signInStep}`,
        );
      }
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function copySecret() {
    try {
      await navigator.clipboard.writeText(sharedSecret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    } catch {
      // Best-effort; ignore.
    }
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="space-y-5">
      <div>
        <h2 className="text-lg font-medium">Set up two-factor authentication</h2>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
          Hearth requires a TOTP authenticator (Google Authenticator, 1Password,
          Authy, etc.). Scan the QR code below, then enter the 6-digit code
          shown by the app.
        </p>
      </div>

      <div className="flex flex-col items-center gap-3 rounded-lg border border-zinc-200 p-4 dark:border-zinc-800">
        <div
          role="img"
          aria-label="TOTP setup QR code; scan with your authenticator app"
          className="rounded-md bg-white p-2"
        >
          <QRCodeSVG value={setupUri} size={200} level="M" />
        </div>
        <details className="w-full text-sm text-zinc-600 dark:text-zinc-400">
          <summary className="cursor-pointer">Can't scan? Enter manually.</summary>
          <dl className="mt-2 space-y-1">
            <div className="flex items-center gap-2">
              <dt className="font-medium">Account:</dt>
              <dd>Hearth ({accountName})</dd>
            </div>
            <div className="flex items-center gap-2">
              <dt className="font-medium">Secret:</dt>
              <dd className="break-all font-mono text-xs">{sharedSecret}</dd>
            </div>
          </dl>
          <button
            type="button"
            onClick={() => void copySecret()}
            className="mt-2 rounded border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
          >
            {secretCopied ? "Copied" : "Copy secret"}
          </button>
        </details>
      </div>

      <div>
        <label htmlFor="mfa-setup-code" className="mb-1 block text-sm font-medium">
          Enter the 6-digit code from your authenticator
        </label>
        <input
          id="mfa-setup-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          autoFocus
          className="w-full rounded-md border border-zinc-300 px-3 py-2 text-center font-mono text-2xl tracking-widest focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
          disabled={submitting}
          placeholder="000000"
        />
      </div>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <button
        type="submit"
        disabled={submitting || code.length !== 6}
        className="w-full rounded-md bg-brand-500 px-4 py-2.5 text-base font-medium text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? "Verifying…" : "Verify and finish setup"}
      </button>
    </form>
  );
}
